import Navbar from 'src/components/HorizontalLayout/Navbar';
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledAccordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TeamCardLoader from '../../../components/Common/TeamCardLoader';
import CardContact from "../../../pages/Users/TeamUsers/CardContact";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import Select from "react-select";
import { getClients, getUsers as onGetUsers } from "../../../slices/thunks";
import { useAgency } from 'src/components/Common/agencyBrandingData';
import { map } from "lodash";
import { usePageAccess, PAGE_ACCESS_TYPES } from '../../../components/Common/useAuthRedirect';
import Pagination from 'src/components/Common/Pagination';
import useIsMobile from 'src/hooks/useIsMobile';
import debounce from 'lodash.debounce';

const TeamUsers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [usersPerPage, setUsersPerPage] = useState(10);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const agency = useAgency();
  const { clients } = useSelector((state: any) => state.clients);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");

  const selectLeadData = createSelector(
    (state: any) => state.contacts.users,
    (users) => users
  );
  const users = useSelector(selectLeadData);

  usePageAccess(PAGE_ACCESS_TYPES.AGENCY_ADMIN_ONLY);



  const fetchUsers = useCallback(() => {
    setLoading(true);
    return dispatch(onGetUsers({
      agency_id: agency.data.Id,
      page: 1,
      limit: 500, // Reduced from 1000 to 500
      search: ""
    })).finally(() => setLoading(false));
  }, [dispatch, agency.data.Id]);

  useEffect(() => {
    fetchUsers();
    // Reduce initial client load to 200 with pagination support
    dispatch(getClients(agency.data.agency_id ?? agency.data.Id, navigate, 1, 200, "", true));
  }, [fetchUsers, dispatch, agency.data.agency_id, navigate]);

  useEffect(() => {
    setCurrentPage(1);
    setAdminCurrentPage(1);
  }, [globalSearchQuery, selectedClientId]);

  const filterUsers = (type: string) => {
    const q = globalSearchQuery.toLowerCase();
    const userData = users?.data || [];
    return userData.filter((user: any) => {
      const userClientIds = user.uds_client_id 
        ? user.uds_client_id.split(',').map((id: string) => id.trim()) 
        : [];
      
      const matchesClientFilter = selectedClientId === null || 
        userClientIds.includes(String(selectedClientId));
      
      const matchesSearchQuery = 
        user.name?.toLowerCase().includes(q) ||
        user.email?. toLowerCase().includes(q) ||
        user.mobile?.toLowerCase().includes(q);
      
      return user.permission_type === type && 
             matchesClientFilter && 
             matchesSearchQuery;
    });
  };

  const isMobile = useIsMobile();

  const adminUsers = useMemo(() => filterUsers("admin"), [users, globalSearchQuery, selectedClientId]);
  const standardUsers = useMemo(() => filterUsers("standard"), [users, globalSearchQuery, selectedClientId]);

  const adminTotalPages = Math.ceil(adminUsers.length / usersPerPage);
  const standardTotalPages = Math.ceil(standardUsers.length / usersPerPage);

  const adminCurrentUsers = adminUsers.slice(
    (adminCurrentPage - 1) * usersPerPage,
    adminCurrentPage * usersPerPage
  );

  const currentStandardUsers = standardUsers.slice(
    (currentPage - 1) * usersPerPage,
    currentPage * usersPerPage
  )

  const DelayedMessage = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
      <div className="text-center">
        <i className="mdi mdi-account-alert-outline" style={{ fontSize: "48px", color: "#6c757d" }}></i>
        <p style={{ fontSize: "18px", color: "#6c757d", marginTop: "10px" }}>No users found</p>
      </div>
    </div>
  );

  const handleClientChange = (selected: any) => {
    setSelectedClientId(selected?.value || null);
  };

  // Debounced search to avoid excessive API calls
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      setGlobalSearchQuery(searchValue);
    }, 300),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedSearch(value);
  };



  return (
    <React.Fragment>
      <style>
        {`
          /* Optional: also remove default box-shadow and background */
          .accordion-button {
            background-color: transparent !important;
            box-shadow: none !important;
          }
        `}
      </style>
      <div className="page-content" style={{ marginTop: "0" }}>
        <Navbar />
        <Container fluid style={{ marginTop: 10, marginBottom: 10 }}>
          <Breadcrumbs title="Settings" breadcrumbItem="Team" titleLink="/branding" showLeftTitle={false}/>
          <Card className="mb-3">
            <CardBody 
              style={{ padding: '8px 5px'}}
            >
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div className="select-wrapper" style={{ width: isMobile ?  '100%' : '240px' }}>
                    <Select
                      name="client"
                      onChange={handleClientChange}
                      options={
                        clients?.data?.map((client: any) => ({
                          value: client.id,
                          label: client.client_name,
                        })) || []
                      }
                      className="dropdown custom-select shadow-sm rounded w-100"
                      classNamePrefix="react-select"
                      isSearchable={true}
                      placeholder="Select Clients"
                      isClearable={true}
                      styles={{
                        input: (provided:  any) => ({
                          ...provided,
                          fontSize: '16px',
                        }),
                        menu: (provided:  any) => ({
                          ...provided,
                          zIndex: 1050,
                          overflowX: "hidden",
                          maxHeight: "300px",
                        }),
                        menuList: (base:  any) => ({
                          ...base,
                          paddingTop: '0px',
                          paddingBottom: '0px',
                        }),
                        option: (base: any) => ({
                          ...base,
                          whiteSpace:'normal',
                          wordBreak:'break-word',
                          backgroundColor: 'transparent',
                          color: 'inherit',
                          ':active': {
                            backgroundColor: 'transparent',
                          },
                        }),
                      }}
                    />
                  </div>
                  
                  <div className="d-flex flex-wrap align-items-center gap-3 flex-grow-1 flex-md-grow-0">
                    <div style={{ width: isMobile ?  '100%' : '300px' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search all users..."
                        onChange={handleSearchChange}
                      />
                    </div>
                    <button
                      className="btn btn-primary button-primary flex-grow-1 flex-md-grow-0"
                      onClick={() => navigate("/add-edit-user", { state: { from:  "/team" } })}
                    >
                      <i className="uil uil-plus me-1"></i> Add New Team Member
                    </button>
                  </div>
                </div>
            </CardBody>
          </Card>
          {/* @ts-ignore */}
          <UncontrolledAccordion defaultOpen={['1', '2']} stayOpen
          >
            {/* ADMIN USERS */}
            <Card className="mb-3" style={{ borderRadius: '8px', overflow: 'hidden' }}>
              <AccordionItem
                style={{
                  border: "none",
                  boxShadow: "none",
                  background: "transparent",
                  padding: 0
                }}
              >
                <AccordionHeader targetId="2" className='custom-border-bottom'>
                  <div className="d-flex flex-column flex-md-row justify-content-between w-100  align-items-start align-items-md-center gap-3" style={{ marginRight: "10px" }}>
                    <h5 className="mb-0" style={{ fontSize: "16px" }}>
                      Agency Users <span className="text-muted fw-normal">({adminUsers.length})</span>
                    </h5>
                  </div>
                </AccordionHeader>
                <AccordionBody accordionId="2" >
                  <Row className='align-items-stretch'>
                    {loading ? <TeamCardLoader count={12} /> :
                      adminCurrentUsers.length > 0 ?
                        map(adminCurrentUsers, (user, key) => (
                          <CardContact key={`admin_${key}`} user={user} agency={agency} onUserDeleted={fetchUsers} />
                        )) : <DelayedMessage />}
                  </Row>
                  {/* Pagination */}
                  {adminCurrentUsers.length > 0 &&
                    <Pagination
                      totalPages={adminTotalPages}
                      currentPage={adminCurrentPage}
                      onPageChange={(page) => setAdminCurrentPage(page)}
                      currentCount={adminCurrentUsers.length}
                      pageSize={usersPerPage}
                      onPageSizeChange={(size) => {
                        setUsersPerPage(size);
                        setAdminCurrentPage(1);
                        setCurrentPage(1); // Reset standard users page as well
                      }}
                      menuPlacement='top'
                    />
                  }
                </AccordionBody>
              </AccordionItem>
            </Card>
            {/* STANDARD USERS */}
            <Card className="mb-3" style={{ borderRadius: '8px', overflow: 'hidden' }}>
              <AccordionItem
                style={{
                  border: "none",
                  boxShadow: "none",
                  background: "transparent",
                  padding: 0
                }}
              >
                <AccordionHeader targetId="1" className='custom-border-bottom'>
                  <div className="d-flex flex-column flex-md-row justify-content-between w-100 align-items-start align-items-start align-items-md-center gap-3" style={{ marginRight: "10px" }}>
                    <h5 className="mb-0" style={{ fontSize: "16px" }}>
                      Client Users <span className="text-muted fw-normal">({standardUsers.length})</span>
                    </h5>
                  </div>
                </AccordionHeader>
                <AccordionBody accordionId="1">
                  <Row>
                    {loading ? <TeamCardLoader count={12} /> :
                      currentStandardUsers.length > 0 ?
                        map(currentStandardUsers, (user, key) => (
                          <CardContact key={`user_${key}`} user={user} agency={agency} onUserDeleted={fetchUsers} />
                        )) : <DelayedMessage />}
                  </Row>
                  {currentStandardUsers.length > 0 &&
                    <Pagination
                      totalPages={standardTotalPages}
                      currentPage={currentPage}
                      onPageChange={(page) => setCurrentPage(page)}
                      currentCount={currentStandardUsers.length}
                      pageSize={usersPerPage}
                      onPageSizeChange={(size) => {
                        setUsersPerPage(size);
                        setAdminCurrentPage(1);
                        setCurrentPage(1);
                      }}
                      menuPlacement='top'
                    />
                  }
                </AccordionBody>
              </AccordionItem>
            </Card>
          </UncontrolledAccordion>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TeamUsers;
